import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Alert,
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Switch,
  Dialog,
  MenuItem,
  Container,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  LinearProgress,
  FormControlLabel,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useSettings from '../../../hooks/useSettings';
import useIsFullAdmin from 'src/hooks/useIsFullAdmin';
// components
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
// data
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { siteBase } from 'src/utils/siteUrl';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import useContentReorder from '../content-order/useContentReorder';
import ReorderControls from '../content-order/ReorderControls';
import OrderErrorAlert from '../content-order/OrderErrorAlert';
import {
  deleteErrorText,
  itemErrorText,
  itemFromConflict,
  mergeScope,
  patchBody,
  positionsBy,
} from '../content-order/reorderModel';
import BuiltInsCard, { CompanySection, SECTION_PAGE, sectionPageUrl } from './BuiltInsCard';

// ---------------------------------------------------------------------------
// Company Profile content — admin-managed items. One collection, four sections:
//   partner  → the partners logo wall
//   diploma  → Kayfa for Learners programs
//   company  → Kayfa for Companies tracks
//   story    → success stories
// Until a section's built-in items are imported (BuiltInsCard), the page shows
// its built-in items first and these items after them. After the import the
// page shows this list only. Order = the arrows of one section (PUT
// /company-extras/reorder); never a typed number.
// ---------------------------------------------------------------------------

type Section = CompanySection;

type Extra = {
  _id?: string;
  section: Section;
  title?: string;
  title_ar?: string;
  subtitle?: string;
  subtitle_ar?: string;
  description?: string;
  description_ar?: string;
  image?: string;
  link?: string;
  tools?: string[];
  points?: string[];
  points_ar?: string[];
  outcome?: string;
  outcome_ar?: string;
  icon?: string;
  category?: string;
  category_ar?: string;
  year?: string;
  order?: number;
  active?: boolean;
  // Set on a copy of a built-in page item (import): hide it, never delete it.
  builtInKey?: string;
  updatedAt?: string;
};

const SECTIONS: { value: Section; label: string; hint: string }[] = [
  { value: 'partner', label: 'Partner logo', hint: 'A logo on the “Our success partners” wall.' },
  { value: 'diploma', label: 'Learner diploma', hint: 'A card in “Kayfa for Learners” products.' },
  { value: 'company', label: 'Company track', hint: 'A card in “Kayfa for Companies” tracks.' },
  { value: 'story', label: 'Success story', hint: 'A card in the “Success stories” section.' },
];
const sectionLabel = (s?: string) => SECTIONS.find((x) => x.value === s)?.label || s || '';

// Company track icons — must match the FE company ICON map keys.
const COMPANY_ICONS = ['chart', 'users', 'data', 'spark', 'cap', 'globe', 'shield', 'target', 'rocket', 'star', 'ai', 'design'];

// No `order`: a new item is appended at the end of its section by the API.
const EMPTY: Extra = { section: 'partner', active: true, tools: [], points: [], points_ar: [] };

const ALL_HINT = 'Pick a section to change its order.';

// Built-in images are site-relative paths (/assets/…): preview them from the site.
const previewSrc = (src?: string) => (src && src.startsWith('/') ? `${siteBase()}${src}` : src);

const toLines = (arr?: string[]) => (Array.isArray(arr) ? arr.join('\n') : '');
const fromLines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);
const toCsv = (arr?: string[]) => (Array.isArray(arr) ? arr.join(', ') : '');
const fromCsv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

// The fields the dialog edits, as the API stores them (lists from their text buffers).
const fieldsOf = (f: Extra, tools: string, points: string, pointsAr: string) => ({
  section: f.section,
  title: f.title?.trim() || '',
  title_ar: f.title_ar?.trim() || '',
  subtitle: f.subtitle?.trim() || '',
  subtitle_ar: f.subtitle_ar?.trim() || '',
  description: f.description?.trim() || '',
  description_ar: f.description_ar?.trim() || '',
  image: f.image?.trim() || '',
  link: f.link?.trim() || '',
  tools: fromCsv(tools),
  points: fromLines(points),
  points_ar: fromLines(pointsAr),
  outcome: f.outcome?.trim() || '',
  outcome_ar: f.outcome_ar?.trim() || '',
  icon: f.icon?.trim() || '',
  category: f.category?.trim() || '',
  category_ar: f.category_ar?.trim() || '',
  year: f.year?.trim() || '',
  active: f.active !== false,
});

const storedFieldsOf = (r: Extra) =>
  fieldsOf({ ...EMPTY, ...r }, toCsv(r.tools), toLines(r.points), toLines(r.points_ar));

// ---------------------------------------------------------------------------

export default function CompanyProfileContent() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const canDelete = useIsFullAdmin();

  const [rows, setRows] = useState<Extra[]>([]);
  const [loading, setLoading] = useState(false);
  // One section at a time by default: its order can be changed; "All" cannot.
  const [filter, setFilter] = useState<Section | 'all'>('partner');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<Extra>(EMPTY);
  // Comma/newline buffers so the raw text isn't reparsed on every keystroke.
  const [toolsText, setToolsText] = useState('');
  const [pointsText, setPointsText] = useState('');
  const [pointsArText, setPointsArText] = useState('');
  const [deleteState, setDeleteState] = useState<{ open: boolean; id?: string }>({ open: false });
  const [deleting, setDeleting] = useState(false);
  // The row as the dialog opened it (re-read from the API): the base of the
  // changed-fields PATCH and of its version check.
  const [base, setBase] = useState<Extra | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [importState, setImportState] = useState<{ open: boolean; section?: Section }>({ open: false });
  const [importing, setImporting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.COMPANY_EXTRAS.LIST);
      const data = res.data?.data ?? res.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load items', { variant: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.section === filter)),
    [rows, filter],
  );
  // "#" = the position inside the item's own section.
  const positions = useMemo(() => positionsBy(rows, (r) => r.section), [rows]);
  const imported = useMemo(() => {
    const out: Record<Section, boolean> = { partner: false, diploma: false, company: false, story: false };
    rows.forEach((r) => {
      if (r.builtInKey) out[r.section] = true;
    });
    return out;
  }, [rows]);

  // The answer is one section's items in their new order: they go back into
  // that section's slots of the full list.
  const applySection = useCallback((scopeRows: Extra[]) => {
    if (!scopeRows.length) return;
    const { section } = scopeRows[0];
    setRows((all) => mergeScope(all, (r) => r.section === section, scopeRows));
  }, []);
  const reorder = useContentReorder<Extra>({
    url: APIS.COMPANY_EXTRAS.REORDER,
    apply: applySection,
    reload: fetchRows,
  });
  const canReorder = filter !== 'all' && !loading;

  const fillForm = (r: Extra) => {
    setForm({ ...EMPTY, ...r });
    setToolsText(toCsv(r.tools));
    setPointsText(toLines(r.points));
    setPointsArText(toLines(r.points_ar));
  };

  const openNew = () => {
    const section: Section = filter === 'all' ? 'partner' : filter;
    setForm({ ...EMPTY, section });
    setBase(null);
    setFormError(null);
    setToolsText('');
    setPointsText('');
    setPointsArText('');
    setFormOpen(true);
  };
  // The dialog starts from the row as the API holds it NOW (another tab may
  // have changed its link since this list loaded).
  const openEdit = async (r: Extra) => {
    if (!r._id || opening) return;
    setOpening(true);
    try {
      const res = await axiosInstance.get(APIS.COMPANY_EXTRAS.BY_ID(r._id));
      const fresh: Extra = res.data?.data ?? res.data;
      const row = fresh && fresh._id ? fresh : r;
      setBase(row);
      fillForm(row);
      setFormError(null);
      setFormOpen(true);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not load this item. Try again.', { variant: 'error' });
    } finally {
      setOpening(false);
    }
  };
  const setField = (k: keyof Extra, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (file?: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosInstance.post(APIS.UPLOAD.MEDIA, fd);
      const url = res.data?.url ?? res.data?.data?.url;
      if (url) setField('image', url);
      else enqueueSnackbar('Upload returned no URL', { variant: 'error' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Upload failed', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const sec = form.section;
    if (sec === 'partner' && !form.image) {
      enqueueSnackbar('A partner needs a logo image', { variant: 'warning' });
      return;
    }
    if (sec !== 'partner' && !form.title?.trim()) {
      enqueueSnackbar('Title is required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    setFormError(null);
    const fields = fieldsOf(form, toolsText, pointsText, pointsArText);
    try {
      // An edit sends only what changed + `section` (critic C1) + the version
      // it opened; never `order`.
      if (form._id && base) {
        const body = patchBody('companyExtra', { ...storedFieldsOf(base), updatedAt: base.updatedAt }, fields);
        if (Object.keys(body).every((k) => k === 'section' || k === 'expectedUpdatedAt')) {
          setFormOpen(false);
          return;
        }
        await axiosInstance.patch(APIS.COMPANY_EXTRAS.PATCH(form._id), body);
      } else await axiosInstance.post(APIS.COMPANY_EXTRAS.ADD, fields);
      enqueueSnackbar(form._id ? 'Item updated' : 'Item created');
      setFormOpen(false);
      // The fresh `updatedAt` of the row is the token of its next edit.
      await fetchRows();
    } catch (e: any) {
      // Someone changed this item meanwhile: their version goes into the form.
      const latest = itemFromConflict<Extra>(e);
      if (latest) {
        setBase(latest);
        fillForm(latest);
      }
      setFormError(itemErrorText(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.id) return;
    setDeleting(true);
    setListError(null);
    try {
      await axiosInstance.delete(APIS.COMPANY_EXTRAS.DELETE(deleteState.id));
      enqueueSnackbar('Item deleted');
      setDeleteState({ open: false });
      await fetchRows();
    } catch (e: any) {
      setListError(deleteErrorText(e));
    } finally {
      setDeleting(false);
    }
  };

  // Copies the section's built-in page items into the list (admin, super-admin).
  const handleImport = async () => {
    const { section } = importState;
    if (!section) return;
    setImporting(true);
    setListError(null);
    try {
      const res = await axiosInstance.post(APIS.COMPANY_EXTRAS.IMPORT_BUILTINS, { section });
      const body = res.data?.data ?? res.data;
      await fetchRows();
      enqueueSnackbar(
        body?.created
          ? `Imported ${body.created} built-in items. They come first; your items follow.`
          : 'Already imported — nothing changed.'
      );
    } catch (e: any) {
      setListError(itemErrorText(e, 'Could not import the built-in items. Nothing was changed.'));
    } finally {
      setImporting(false);
    }
  };

  const sec = form.section;
  const hasImage = sec === 'partner' || sec === 'story';
  const hasKind = sec === 'diploma' || sec === 'company' || sec === 'story';
  const hasDesc = sec !== 'partner';
  const hasTools = sec === 'diploma' || sec === 'company';
  const hasPoints = sec === 'company';
  const hasOutcome = sec === 'company';
  const hasIcon = sec === 'company';
  const hasStoryMeta = sec === 'story';
  const hasLink = sec === 'partner';
  const kindLabel =
    sec === 'diploma' ? 'Label (e.g. "Diploma · 5 months")'
    : sec === 'company' ? 'Format (e.g. "On-site / Remote")'
    : 'Role / subtitle';

  return (
    <Page title="Company Profile">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Company Profile"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Company Profile' }]}
          action={
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openNew}>
              New item
            </Button>
          }
        />

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Pick a section to see its items in page order. Use the arrows to change the order — it is
            saved at once. Keep Active on to publish an item; turn it off to hide it. New items are
            added at the end of their section.
          </Typography>
        </Card>

        <Card sx={{ p: 2, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Show section"
            value={filter}
            onChange={(e) => {
              reorder.clearError();
              setListError(null);
              setFilter(e.target.value as Section | 'all');
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">All sections</MenuItem>
            {SECTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
        </Card>

        <BuiltInsCard
          section={filter === 'all' ? null : filter}
          sections={SECTIONS}
          imported={imported}
          canImport={canDelete}
          importing={importing}
          onImport={(section) => setImportState({ open: true, section })}
        />

        <OrderErrorAlert
          error={reorder.error || listError}
          saving={reorder.saving}
          onClose={() => {
            reorder.clearError();
            setListError(null);
          }}
          hint={filter === 'all' ? ALL_HINT : null}
        />

        <Card>
          <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
            {loading && (
              <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }} />
            )}
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 56 }}>#</TableCell>
                  <TableCell sx={{ width: 120 }}>Order</TableCell>
                  <TableCell sx={{ width: 72 }}>Preview</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      No items yet — click “New item” to add one.
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((r, i) => (
                  <TableRow key={r._id} hover>
                    <TableCell align="center">{positions.get(String(r._id)) ?? i + 1}</TableCell>
                    <TableCell>
                      <ReorderControls
                        name={r.title || (r.section === 'partner' ? `logo ${i + 1}` : `item ${i + 1}`)}
                        index={i}
                        count={visible.length}
                        disabled={!canReorder || reorder.saving}
                        disabledHint={filter === 'all' ? ALL_HINT : undefined}
                        onMove={(kind) => reorder.move(visible, i, kind, { section: filter })}
                      />
                    </TableCell>
                    <TableCell>
                      {r.image ? (
                        <Avatar
                          src={previewSrc(r.image)}
                          variant="rounded"
                          sx={{ width: 44, height: 44, bgcolor: 'background.neutral', '& img': { objectFit: 'contain' } }}
                        />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 44, height: 44, bgcolor: 'background.neutral', color: 'text.disabled' }}>
                          <Iconify icon="eva:file-text-outline" />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Label color="info">{sectionLabel(r.section)}</Label>
                      {SECTION_PAGE[r.section] ? (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          <a href={sectionPageUrl(r.section)} target="_blank" rel="noopener noreferrer">
                            {SECTION_PAGE[r.section].path}
                          </a>
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" noWrap>{r.title || (r.section === 'partner' ? '(logo)' : '—')}</Typography>
                        {r.builtInKey ? <Label color="default">Built-in</Label> : null}
                      </Stack>
                      {r.subtitle ? (
                        <Typography variant="caption" color="text.secondary" noWrap>{r.subtitle}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="center">
                      <Label color={r.active !== false ? 'success' : 'default'}>
                        {r.active !== false ? 'Active' : 'Hidden'}
                      </Label>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => openEdit(r)} disabled={opening}>
                        <Iconify icon="eva:edit-fill" />
                      </IconButton>
                      {canDelete && (
                        <IconButton sx={{ color: 'error.main' }} onClick={() => setDeleteState({ open: true, id: r._id })}>
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Add / edit dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form._id ? 'Edit item' : 'New item'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}
            <TextField
              select
              label="Section"
              value={form.section}
              onChange={(e) => setField('section', e.target.value)}
              fullWidth
              disabled={!!form._id}
              helperText={
                form._id
                  ? `${SECTIONS.find((s) => s.value === form.section)?.hint || ''} The section of a saved item stays as it is.`
                  : `${SECTIONS.find((s) => s.value === form.section)?.hint || ''} New items are added at the end of the section.`
              }
            >
              {SECTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>

            {hasImage && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={previewSrc(form.image)}
                  variant="rounded"
                  sx={{ width: 64, height: 64, bgcolor: 'background.neutral', '& img': { objectFit: 'contain' } }}
                >
                  <Iconify icon="eva:image-outline" />
                </Avatar>
                <Button variant="outlined" component="label" disabled={uploading} startIcon={<Iconify icon="eva:cloud-upload-outline" />}>
                  {uploading ? 'Uploading…' : sec === 'partner' ? 'Upload logo' : 'Upload photo'}
                  <input hidden type="file" accept="image/*" onChange={(e) => handleUpload(e.target.files?.[0])} />
                </Button>
              </Stack>
            )}

            <TextField
              label={sec === 'partner' ? 'Name (for alt text, optional)' : 'Title (English)'}
              value={form.title || ''}
              onChange={(e) => setField('title', e.target.value)}
              fullWidth
              required={sec !== 'partner'}
            />
            {sec !== 'partner' && (
              <TextField label="Title (Arabic)" value={form.title_ar || ''} onChange={(e) => setField('title_ar', e.target.value)} fullWidth inputProps={{ dir: 'rtl' }} />
            )}

            {hasKind && (
              <>
                <TextField label={kindLabel} value={form.subtitle || ''} onChange={(e) => setField('subtitle', e.target.value)} fullWidth />
                <TextField label="Arabic label" value={form.subtitle_ar || ''} onChange={(e) => setField('subtitle_ar', e.target.value)} fullWidth inputProps={{ dir: 'rtl' }} />
              </>
            )}

            {hasDesc && (
              <>
                <TextField label="Description (English)" value={form.description || ''} onChange={(e) => setField('description', e.target.value)} fullWidth multiline minRows={2} />
                <TextField label="Description (Arabic)" value={form.description_ar || ''} onChange={(e) => setField('description_ar', e.target.value)} fullWidth multiline minRows={2} inputProps={{ dir: 'rtl' }} />
              </>
            )}

            {hasIcon && (
              <TextField select label="Icon" value={form.icon || ''} onChange={(e) => setField('icon', e.target.value)} fullWidth>
                <MenuItem value=""><em>Default</em></MenuItem>
                {COMPANY_ICONS.map((k) => (
                  <MenuItem key={k} value={k}>{k}</MenuItem>
                ))}
              </TextField>
            )}

            {hasTools && (
              <TextField
                label="Tags / tools (comma-separated)"
                value={toolsText}
                onChange={(e) => setToolsText(e.target.value)}
                fullWidth
                placeholder="Python, SQL, Power BI"
              />
            )}

            {hasPoints && (
              <>
                <TextField label="Modules (one per line)" value={pointsText} onChange={(e) => setPointsText(e.target.value)} fullWidth multiline minRows={3} />
                <TextField label="Modules — Arabic (one per line)" value={pointsArText} onChange={(e) => setPointsArText(e.target.value)} fullWidth multiline minRows={3} inputProps={{ dir: 'rtl' }} />
              </>
            )}

            {hasOutcome && (
              <>
                <TextField label="Outcome (English)" value={form.outcome || ''} onChange={(e) => setField('outcome', e.target.value)} fullWidth />
                <TextField label="Outcome (Arabic)" value={form.outcome_ar || ''} onChange={(e) => setField('outcome_ar', e.target.value)} fullWidth inputProps={{ dir: 'rtl' }} />
              </>
            )}

            {hasStoryMeta && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                <TextField label="Category (e.g. Event)" value={form.category || ''} onChange={(e) => setField('category', e.target.value)} fullWidth />
                <TextField label="Category (Arabic)" value={form.category_ar || ''} onChange={(e) => setField('category_ar', e.target.value)} fullWidth inputProps={{ dir: 'rtl' }} />
                <TextField label="Year" value={form.year || ''} onChange={(e) => setField('year', e.target.value)} sx={{ maxWidth: 120 }} />
              </Stack>
            )}

            {hasLink && (
              <TextField label="Website / verify link (optional)" value={form.link || ''} onChange={(e) => setField('link', e.target.value)} fullWidth placeholder="https://…" />
            )}

            <Stack direction="row" spacing={2.5} alignItems="center">
              <FormControlLabel
                control={<Switch checked={form.active !== false} onChange={(e) => setField('active', e.target.checked)} />}
                label="Active"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || uploading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isOpen={deleteState.open}
        onClose={() => setDeleteState({ open: false })}
        confirmAction={handleDelete}
        headText="Delete item"
        text="Are you sure you want to delete this Company Profile item?"
        loading={deleting}
      />

      <ConfirmationDialog
        isOpen={importState.open}
        onClose={() => setImportState({ open: false })}
        confirmAction={handleImport}
        headText="Import the built-in items"
        text={
          importState.section
            ? `Copy the ${sectionLabel(importState.section).toLowerCase()} section's built-in page items into this list? They come first, in the page's order, and your items follow, so the page looks the same. From then on the page shows this list only and you can order, edit or hide every item. Undoing it needs a developer.`
            : ''
        }
        loading={importing}
      />
    </Page>
  );
}
